import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import CircleIcon from '@mui/icons-material/Circle';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { Link } from 'react-router-dom';
import MachineAdminShell from './MachineAdminShell';
import useMockMachineObserver from './useMockMachineObserver';

function statusTone(state) {
  if (state === 'alarm') return { border: '#ff5f57', bg: 'rgba(239,68,68,0.14)', chipBg: 'rgba(239,68,68,0.18)' };
  if (state === 'warning') return { border: '#ffb44c', bg: 'rgba(245,158,11,0.12)', chipBg: 'rgba(245,158,11,0.18)' };
  return { border: '#4ade80', bg: 'rgba(34,197,94,0.12)', chipBg: 'rgba(34,197,94,0.18)' };
}

function statusIcon(state) {
  if (state === 'alarm') return <ErrorRoundedIcon sx={{ color: '#ff5f57', fontSize: 18 }} />;
  if (state === 'warning') return <WarningAmberRoundedIcon sx={{ color: '#ffb44c', fontSize: 18 }} />;
  return <CheckCircleRoundedIcon sx={{ color: '#4ade80', fontSize: 18 }} />;
}

function MachineCard({ machine }) {
  const tone = statusTone(machine.state);
  const trend = machine.miniTrend;

  return (
    <Paper
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: `1px solid ${alpha(tone.border, 0.4)}`,
        background: `linear-gradient(180deg, ${tone.bg}, rgba(15, 23, 32, 0.95))`,
        color: '#f8fafc',
        minHeight: 320,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            {machine.name}
          </Typography>
          <Chip
            size="small"
            icon={statusIcon(machine.state)}
            label={machine.shortStatus}
            sx={{
              height: 28,
              color: '#fff',
              bgcolor: tone.chipBg,
              border: `1px solid ${alpha(tone.border, 0.3)}`,
              '.MuiChip-icon': { ml: 0.75 },
            }}
          />
        </Box>
        <Chip
          size="small"
          label={`${machine.activeAlerts} Alarm`}
          sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.82)' }}
        />
      </Stack>

      <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.75)', minHeight: 42 }}>
        {machine.summary}
      </Typography>

      <Stack spacing={1.1} sx={{ mt: 2.5 }}>
        {[
          { icon: <ElectricBoltOutlinedIcon sx={{ color: '#fbbf24', fontSize: 18 }} />, label: 'Elektrik Gücü', value: machine.power },
          { icon: <AirOutlinedIcon sx={{ color: '#7dd3fc', fontSize: 18 }} />, label: 'Hava Akışı', value: machine.airflowInstant },
          { icon: <SpeedOutlinedIcon sx={{ color: '#fca5a5', fontSize: 18 }} />, label: 'Basınç', value: machine.pressure },
        ].map((item) => (
          <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.2, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.04)' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {item.icon}
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                {item.label}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ fontWeight: 800 }}>
              {item.value}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ mt: 2.5, mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)' }}>
          Son veri {machine.lastSeen}
        </Typography>
        <Box sx={{ mt: 1.25, height: 72 }}>
          <LineChart
            xAxis={[{ scaleType: 'point', data: trend.map((_, index) => index), disableLine: true, disableTicks: true }]}
            yAxis={[{ disableLine: true, disableTicks: true }]}
            series={[{ data: trend, color: tone.border, curve: 'monotoneX', area: true, showMark: false }]}
            height={72}
            margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
            grid={{ horizontal: false, vertical: false }}
            slotProps={{ legend: { hidden: true } }}
          />
        </Box>
      </Box>

      <Button
        component={Link}
        to={`/machine-admin/machines/${machine.id}`}
        fullWidth
        variant={machine.state === 'alarm' ? 'contained' : 'outlined'}
        endIcon={<ArrowForwardOutlinedIcon />}
        sx={{
          mt: 'auto',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: 2.5,
          color: '#fff',
          borderColor: machine.state === 'alarm' ? tone.border : 'rgba(255,255,255,0.18)',
          bgcolor: machine.state === 'alarm' ? '#1976d2' : 'rgba(255,255,255,0.06)',
          '&:hover': {
            borderColor: machine.state === 'alarm' ? '#1565c0' : 'rgba(255,255,255,0.28)',
            bgcolor: machine.state === 'alarm' ? '#1565c0' : 'rgba(255,255,255,0.1)',
          },
        }}
      >
        Makine detayını aç
      </Button>
    </Paper>
  );
}

export default function MachineAdminMockPage() {
  const { liveMachines, priorityItems } = useMockMachineObserver();

  return (
    <MachineAdminShell
      pageTitle="Genel İzleme"
      pageSubtitle="DANDU FINDIK fabrikasındaki 5 fındık kırma makinesini tek ekrandan tarayın, problemli makineyi seçip detayına inin."
      pageActions={
        <Chip
          icon={<CircleIcon sx={{ color: '#4ade80 !important', fontSize: 12 }} />}
          label="Tüm sensörler 1 sn sıklıkla veri basıyor"
          sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#e2e8f0' }}
        />
      }
    >
      <Paper sx={{ p: 2.25, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{ color: '#93c5fd', letterSpacing: 1.2 }}>
              Operatör Akışı
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
              Önce tüm makineleri tarayın, sonra problemli makinenin detay ekranına inin.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            {[
              'Alarm odaklı tarama',
              '2 sn UI yenileme',
              'Makine bazlı detay ekranı',
            ].map((item) => (
              <Chip key={item} label={item} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#dbeafe' }} />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Box>
        <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1.2 }}>
          Öncelikli Durumlar
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.25 }}>
          {priorityItems.map((item) => (
            <Paper
              key={item.id}
              component={Link}
              to={item.to}
              sx={{
                display: 'block',
                p: 2,
                borderRadius: 3,
                textDecoration: 'none',
                bgcolor: alpha(item.tone, 0.1),
                border: `1px solid ${alpha(item.tone, 0.35)}`,
                color: '#fff',
                transition: 'transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  borderColor: alpha(item.tone, 0.6),
                  bgcolor: alpha(item.tone, 0.16),
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <NotificationsActiveOutlinedIcon sx={{ color: item.tone }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', mt: 0.35 }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={item.chip}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontWeight: 700,
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
        {liveMachines.map((machine) => (
          <MachineCard key={machine.id} machine={machine} />
        ))}
      </Box>
    </MachineAdminShell>
  );
}
